/**
 * ZIP code lookup utility using us-zips package
 * Provides fast client-side ZIP code to coordinates lookup
 */
// @ts-ignore - us-zips is CommonJS, may need special handling
import usZips from 'us-zips';

export interface ZipCodeInfo {
  zip: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
}

// Lazy load and validate us-zips
let zipData: any = null;
function getZipData() {
  if (!zipData) {
    try {
      // Check if usZips is actually loaded
      if (!usZips || typeof usZips !== 'object') {
        console.error('❌ [zipCodeLookup] us-zips is not a valid object:', typeof usZips, usZips);
        zipData = {};
        return zipData;
      }
      zipData = usZips;
      const zipCount = Object.keys(zipData).length;
      console.log('✅ [zipCodeLookup] us-zips loaded, total ZIP codes:', zipCount);
      if (zipCount === 0) {
        console.error('❌ [zipCodeLookup] us-zips loaded but has no ZIP codes!');
      }
    } catch (error) {
      console.error('❌ [zipCodeLookup] Failed to load us-zips:', error);
      zipData = {}; // Fallback to empty object
    }
  }
  return zipData;
}

/**
 * Get ZIP code information by ZIP code string
 */
export function getZipCodeInfo(zipCode: string): ZipCodeInfo | null {
  if (!zipCode || zipCode.trim().length < 5) {
    return null;
  }
  
  try {
    // Normalize ZIP code (handle 5-digit and ZIP+4 formats)
    const normalizedZip = zipCode.trim().substring(0, 5);
    const data = getZipData();
    
    // us-zips package structure: { [zip]: { latitude, longitude } }
    // Note: city and state are not included in this package
    const zipDataEntry = data[normalizedZip];
    
    if (!zipDataEntry || !zipDataEntry.latitude || !zipDataEntry.longitude) {
      return null;
    }
    
    return {
      zip: normalizedZip,
      city: '', // us-zips doesn't include city/state
      state: '', // us-zips doesn't include city/state
      latitude: zipDataEntry.latitude || 0,
      longitude: zipDataEntry.longitude || 0,
    };
  } catch (error) {
    console.error('❌ [zipCodeLookup] Error in getZipCodeInfo:', error);
    return null;
  }
}

/**
 * Search ZIP codes by partial match (for autocomplete)
 */
export function searchZipCodes(query: string, limit: number = 5): ZipCodeInfo[] {
  if (!query || query.trim().length < 2) {
    return [];
  }
  
  try {
    const normalizedQuery = query.trim();
    const results: ZipCodeInfo[] = [];
    const data = getZipData();
    
    // Search through all ZIP codes
    // us-zips package structure: { [zip]: { latitude, longitude } }
    for (const [zip, zipDataEntry] of Object.entries(data)) {
      if (results.length >= limit) break;
      
      // Check if ZIP code starts with query (only match by ZIP code, not city/state)
      if (zip.startsWith(normalizedQuery)) {
        const entry = zipDataEntry as { latitude?: number; longitude?: number };
        if (entry.latitude && entry.longitude) {
          results.push({
            zip: zip,
            city: '', // us-zips doesn't include city/state
            state: '', // us-zips doesn't include city/state
            latitude: entry.latitude,
            longitude: entry.longitude,
          });
        }
      }
    }
    
    return results;
  } catch (error) {
    console.error('❌ [zipCodeLookup] Error in searchZipCodes:', error);
    return [];
  }
}

/**
 * Extract unique ZIP codes from camps data
 * Returns ZIP codes that exist in both camps and us-zips database
 */
export function extractUniqueZipCodesFromCamps(camps: Array<{ zip_code?: string | null }>): ZipCodeInfo[] {
  const zipSet = new Set<string>();
  const zipInfos: ZipCodeInfo[] = [];
  
  for (const camp of camps) {
    if (camp.zip_code && camp.zip_code.trim().length >= 5) {
      const zip = camp.zip_code.trim().substring(0, 5);
      if (!zipSet.has(zip)) {
        zipSet.add(zip);
        const zipInfo = getZipCodeInfo(zip);
        if (zipInfo) {
          zipInfos.push(zipInfo);
        }
      }
    }
  }
  
  return zipInfos;
}

